import { TableContainer, TableCell, TableHead, TableRow, Table, Paper, Stack, TableBody } from "@mui/material";
import formatter from "../../../tools/formatter";
import moment from "moment";
import { taxDesignationToCode, ratingIsHigher } from "../../../constants";
import useDealBuilderSimilarDealsStore, { SimilarDeal } from "../../../stores/deal-builder-similar-deals.store.ts";
import { getStateName, SaleMethod, USState } from "../../services/utils/primary-constants.ts";
import { DealBuilder } from "../../services/deal-builder.ts";
import { ProductionUtils } from "../../services/utils/production.utils.ts";

// Step 1: Get data from the current deal
function inputToBackendFormat(dealBuilder: DealBuilder): any {
  const bonds = dealBuilder.bonds;
  const production = bonds.map(bond => dealBuilder.production.find(x => x.bond === bond));

  return {
    security_code: dealBuilder.securityType,
    offering_date: dealBuilder.marketConditionsDate.toISOString(),
    tax_code: taxDesignationToCode(dealBuilder.taxDesignation),
    state: dealBuilder.stateOfIssuance,
    state_enhancement: dealBuilder.stateEnhancement !== undefined ? 1 : 0,
    total_offering_amount: dealBuilder.totalTargetParAmount,
    spline_lt_rating_composite: dealBuilder.ratingAssumption,
    bond_insurance_code: dealBuilder.bondInsurance ? "YES" : null,
    next_call_date: dealBuilder.callDate === undefined ? null : dealBuilder.callDate.toISOString(),
    competitive: dealBuilder.saleMethod === SaleMethod.Competitive ? 1 : 0,
    maturity_date: dealBuilder.bonds.map(x => moment(x.maturity).format("YYYY-MM-DD")),
    coupon: dealBuilder.bonds.map(x => x.coupon * 100),
    offering_yield: production.map(x => x ? (x.curve + x.spread) * 100 : 0),
    cusip: bonds.map(x => "-"),
    total_maturity_offering_amt: bonds.map(ProductionUtils.getBondPrincipal),
    spline_aaa: production.map(x => x ? x.curve * 100 : 0),
    sink_fund_type: bonds.map(bond => bond.type === "term" ? "T" : ""),
    issuer_long_name: dealBuilder.borrower || "SPLINE PREDICTION",
    issue_description: dealBuilder.issuanceTitle,
    repayment_bucket: "N/A",
    dated_date: null,
    alignments: bonds.map(x => moment(x.maturity).year()),
  }
}

// Step 2: Declare the functions we are going to use to format the backend deal data
const formatDate = (isoDate: string) => isoDate ? moment.utc(isoDate).format("MM/DD/YYYY") : "-";
const formatIssuerName = (text: string) => text.toUpperCase();
const formatIssueDescription = (text: string) => text;
const formatState = (text: keyof typeof USState) => getStateName(text);
const formatAmount = (number: number) => formatter.money(number);
const formatSecurityCode = (text: string) => text;
const formatRating = (text: string) => text;
const formatOfferingDate = (isoDate: string) => formatDate(isoDate);
const formatDatedDate = (isoDate: string) => formatDate(isoDate);
const formatCallDate = (isoDate: string) => formatDate(isoDate);
const formatEnhancement = (number: boolean | number) => number || number === 1 ? "Yes" : "No";
const formatInsurance = (text: string) => text || "-";
const formatSaleMethod = (number: boolean | number) => number || number === 1 ? "Competitive" : "Negotiated";
const formatTaxCode = (text: string) => text;
const formatMaturityDate = (isoDate: string) => formatDate(isoDate);
const formatCoupon = (number: number) => formatter.percent(number / 100);
const formatOfferingYield = (number: number) => formatter.percent(number / 100);
const formatCusip = (text: string) => text;
const formatPrincipal = (number: number) => formatter.money(number);
const formatAAA = (number: number) => formatter.percent(number / 100);
const formatSinkFund = (term: string) => term;
const formatSpread = (number: number) => Math.round(number * 100);

// Step 3: Define the different background colors and their respective text colors.
const DEFAULT_BG = "inherit";
const ORANGE_BG = "#fdb326";
const RED_BG = "#ef3a3a";
const GREEN_BG = "#22cd22";

const DEFAULT_TEXT = "inherit";
const WHITE_TEXT = "white";

const BG_COLOR_MAP = {
  [DEFAULT_BG]: DEFAULT_TEXT,
  [ORANGE_BG]: WHITE_TEXT,
  [RED_BG]: WHITE_TEXT,
  [GREEN_BG]: WHITE_TEXT,
}

// Step 4: Declare the functions we are going to use to color the backend deal data
const colorIssuerName = (_baseLine: string, _text: string) => DEFAULT_BG;
const colorIssueDescription = (_baseLine: string, _text: string) => DEFAULT_BG;
const colorState = (baseLine: keyof typeof USState, text: keyof typeof USState) => getStateName(baseLine) === getStateName(text) ? DEFAULT_BG : ORANGE_BG;
const colorAmount = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorSecurityCode = (baseLine: string, text: string) => baseLine === text ? DEFAULT_BG : ORANGE_BG;
const colorRating = (baseLine: string, text: string) => baseLine === text ? DEFAULT_BG : ratingIsHigher(baseLine, text) ? RED_BG : GREEN_BG;
const colorTaxCode = (baseLine: string, text: string) => baseLine === text ? DEFAULT_BG : ORANGE_BG;
const colorOfferingDate = (_baseLine: string, _text: string) => DEFAULT_BG;
const colorDatedDate = (_baseLine: string, _text: string) => DEFAULT_BG;
const colorCallDate = (_baseLine: string, _text: string) => DEFAULT_BG;
const colorEnhancement = (baseLine: number | boolean, text: number | boolean) => baseLine === text ? DEFAULT_BG : ORANGE_BG;
const colorInsurance = (baseLine: string, text: string) => baseLine === text ? DEFAULT_BG : baseLine === null ? GREEN_BG : RED_BG;
const colorSaleMethod = (baseLine: number | boolean, text: number | boolean) => baseLine === text ? DEFAULT_BG : ORANGE_BG;
const colorMaturityDate = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorCoupon = (baseLine: number, number: number) => baseLine == null || baseLine === number ? DEFAULT_BG : baseLine > number ? RED_BG : GREEN_BG;
const colorCusip = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorOfferingYield = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorPrincipal = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorSplineAAA = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorSinkFund = (_baseLine: number, _number: number) => DEFAULT_BG;
const colorSpread = (_baseLine: number, _number: number) => DEFAULT_BG;

// Step 5: Convert the backend similar deals data into the displayed texts
function createDealData(userDeal: SimilarDeal, similarDeal: SimilarDeal): any {
  const issuerName = formatIssuerName(similarDeal.issuer_long_name)
  const issuerNameBg = colorIssuerName(userDeal.issuer_long_name, similarDeal.issuer_long_name);
  const issueDescription = formatIssueDescription(similarDeal.issue_description)
  const issueDescriptionBg = colorIssueDescription(userDeal.issue_description, similarDeal.issue_description)
  const state = formatState(similarDeal.state as any)
  const stateBg = colorState(userDeal.state as any, similarDeal.state as any)
  const amount = formatAmount(similarDeal.total_offering_amount)
  const amountBg = colorAmount(userDeal.total_offering_amount, similarDeal.total_offering_amount)
  const securityName = formatSecurityCode(similarDeal.security_code)
  const securityNameBg = colorSecurityCode(userDeal.security_code, similarDeal.security_code)
  const rating = formatRating(similarDeal.spline_lt_rating_composite)
  const ratingBg = colorRating(userDeal.spline_lt_rating_composite, similarDeal.spline_lt_rating_composite)
  const offeringDate = formatOfferingDate(similarDeal.offering_date)
  const offeringDateBg = colorOfferingDate(userDeal.offering_date, similarDeal.offering_date)
  const datedDate = formatDatedDate(similarDeal.dated_date)
  const datedDateBg = colorDatedDate(userDeal.dated_date, similarDeal.dated_date)
  const callDate = formatCallDate(similarDeal.next_call_date)
  const callDateBg = colorCallDate(userDeal.next_call_date, similarDeal.next_call_date)
  const enhancement = formatEnhancement(similarDeal.state_enhancement)
  const enhancementBg = colorEnhancement(userDeal.state_enhancement, similarDeal.state_enhancement)
  const insurance = formatInsurance(similarDeal.bond_insurance_code)
  const insuranceBg = colorInsurance(userDeal.bond_insurance_code, similarDeal.bond_insurance_code)
  const saleMethod = formatSaleMethod(similarDeal.competitive)
  const taxCodeBg = colorTaxCode(userDeal.tax_code, similarDeal.tax_code)
  const taxCode = formatTaxCode(similarDeal.tax_code)
  const saleMethodBg = colorSaleMethod(userDeal.competitive, similarDeal.competitive)
  const maturityDates = similarDeal.maturity_date.map(isoDate => formatMaturityDate(isoDate as any));
  const maturityYear = similarDeal.maturity_date.map(isoDate => moment(isoDate).year());
  const coupons = similarDeal.coupon.map(isoDate => formatCoupon(isoDate));
  const cusips = similarDeal.cusip.map(isoDate => formatCusip(isoDate));
  const offeringYields = similarDeal.offering_yield.map(number => number != null ? formatOfferingYield(number) : "-");
  const principals = similarDeal.total_maturity_offering_amt.map(number => formatPrincipal(number));
  const splineAAAs = similarDeal.spline_aaa.map(number => number != null ? formatAAA(number) : "-");
  const sinkFunds = similarDeal.sink_fund_type.map(number => formatSinkFund(number));
  const spreads = similarDeal.offering_yield.map((offeringYield, index) => {
    const aaa = similarDeal.spline_aaa[index];
    return offeringYield != null && aaa != null ? formatSpread(offeringYield - aaa) : "-";
  });

  // The color of the time/dependant field may depend on the alignment with the user deal.
  // We need to find the baseline aligned value to compare.
  const alignments = similarDeal.maturity_date.map((isoDate) => {
    const alignment = moment(isoDate).year();
    return (userDeal as any).alignments.indexOf(alignment)
  });

  const maturityDatesBg = similarDeal.maturity_date.map((isoDate, index) => {
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.maturity_date[baseLineIndex] : null
    return colorMaturityDate(baseLine as any, isoDate)
  });

  const couponsBg = similarDeal.coupon.map((number, index) => {
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.coupon[baseLineIndex] : null
    return colorCoupon(baseLine as any, number)
  });

  const cusipsBg = similarDeal.cusip.map((text, index) => {
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.cusip[baseLineIndex] : null
    return colorCusip(baseLine as any, text as any)
  });

  const offeringYieldsBg = similarDeal.offering_yield.map((number, index) => {
    if (number == null) return DEFAULT_BG;
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.offering_yield[baseLineIndex] : null
    return colorOfferingYield(baseLine as any, number)
  });

  const principalsBg = similarDeal.total_maturity_offering_amt.map((number, index) => {
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.total_maturity_offering_amt[baseLineIndex] : null
    return colorPrincipal(baseLine as any, number)
  });

  const splineAAABg = similarDeal.spline_aaa.map((number, index) => {
    if (number == null) return DEFAULT_BG;
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.spline_aaa[baseLineIndex] : undefined;
    return baseLine != null ? colorSplineAAA(baseLine as any, number) : DEFAULT_BG;
  });

  const sinkFundsBg = similarDeal.sink_fund_type.map((number, index) => {
    const baseLineIndex = alignments[index];
    const baseLine = baseLineIndex !== -1 ? userDeal.sink_fund_type[baseLineIndex] : null
    return colorSinkFund(baseLine as any, number as any)
  });

  const spreadsBg = similarDeal.offering_yield.map((offeringYield, index) => {
    if (offeringYield == null) return DEFAULT_BG;
    const baseLineIndex = alignments[index];
    const userAaa = baseLineIndex !== -1 ? userDeal.spline_aaa[baseLineIndex] : undefined;
    const dealAaa = similarDeal.spline_aaa[index];
    if (userAaa == null || dealAaa == null) return DEFAULT_BG;
    const baseLine = userDeal.offering_yield[baseLineIndex] - userAaa;
    const number = offeringYield - dealAaa;
    return colorSpread(baseLine as any, number);
  });

  return {
    issuerName: { text: issuerName, bg: issuerNameBg },
    issueDescription: { text: issueDescription, bg: issueDescriptionBg },
    state: { text: state, bg: stateBg },
    amount: { text: amount, bg: amountBg },
    securityName: { text: securityName, bg: securityNameBg },
    rating: { text: rating, bg: ratingBg },
    offeringDate: { text: offeringDate, bg: offeringDateBg },
    datedDate: { text: datedDate, bg: datedDateBg },
    callDate: { text: callDate, bg: callDateBg },
    enhancement: { text: enhancement, bg: enhancementBg },
    insurance: { text: insurance, bg: insuranceBg },
    saleMethod: { text: saleMethod, bg: saleMethodBg },
    taxCode: { text: taxCode, bg: taxCodeBg },
    maturityDates: maturityDates.map((_, i) => ({ text: maturityDates[i], bg: maturityDatesBg[i], alignment: alignments[i], year: maturityYear[i] })),
    coupons: coupons.map((_, i) => ({ text: coupons[i], bg: couponsBg[i], alignment: alignments[i], year: maturityYear[i] })),
    cusips: cusips.map((_, i) => ({ text: cusips[i], bg: cusipsBg[i], alignment: alignments[i], year: maturityYear[i] })),
    offeringYields: offeringYields.map((_, i) => ({ text: offeringYields[i], bg: offeringYieldsBg[i], alignment: alignments[i], year: maturityYear[i] })),
    principals: principals.map((_, i) => ({ text: principals[i], bg: principalsBg[i], alignment: alignments[i], year: maturityYear[i] })),
    splineAAAs: splineAAAs.map((_, i) => ({ text: splineAAAs[i], bg: splineAAABg[i], alignment: alignments[i], year: maturityYear[i] })),
    sinkFunds: sinkFunds.map((_, i) => ({ text: sinkFunds[i], bg: sinkFundsBg[i], alignment: alignments[i], year: maturityYear[i] })),
    spreads: spreads.map((_, i) => ({ text: spreads[i], bg: spreadsBg[i], alignment: alignments[i], year: maturityYear[i] }))
  }
}

const EMPTY_DEAL_DATA_KEY = { text: "", bg: "inherit", alignment: 0, year: 0 };

// Step 6: Logic utilities for the deals
const findSmallerAndBiggerYear = (deals: any) => {
  let smaller = Number.MAX_VALUE;
  let bigger = Number.MIN_VALUE;
  for (const deal of deals) {
    for (const date of deal.maturityDates) {
      smaller = Math.min(smaller, date.year);
      bigger = Math.max(bigger, date.year);
    }
  }

  return [smaller, bigger];
}

const getDealData = (deal: any, year: number, key: string) => {
  const dataIndex = deal.maturityDates.findIndex((date: any) => date.year === year);
  if (dataIndex === -1) return EMPTY_DEAL_DATA_KEY;
  return deal[key][dataIndex];
}

interface SimilarDealsTableProps {
  builder: DealBuilder
}

export default function SimilarDealsTable({ builder }: SimilarDealsTableProps) {
  const { similarDeals, selectedSimilarDealIds } = useDealBuilderSimilarDealsStore();

  const userDealBackendFormat: SimilarDeal = inputToBackendFormat(builder);
  const tableUserDeal = createDealData(userDealBackendFormat, userDealBackendFormat);
  const visibleDeals = similarDeals.filter((deal) => selectedSimilarDealIds.includes(deal.issue_id));
  const tableSimilarDeals = visibleDeals.map(deal => createDealData(userDealBackendFormat, deal));
  const [smallerYear, biggerYear] = findSmallerAndBiggerYear([tableUserDeal, ...tableSimilarDeals]);
  const range = (low: number, high: number) => Array.from({ length: high - low + 1 }, (_, i) => i + low);

  return (
    <Stack direction="column" gap={4}>

      <Paper sx={{ width: "fit-content" }}>
        <Stack direction="row" maxWidth="calc(100vw - 390px)">
          {/* Left panel showing the user deal */}
          <Stack direction="column" gap={2} width="535px" sx={{ borderRight: "1px solid #e0e0e0", flexShrink: 0 }}>
            <TableContainer sx={{ width: "100%", maxWidth: "100%" }}>
              <Table>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    align="center"
                    sx={{
                      fontWeight: 500,
                      py: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                    {tableUserDeal.issuerName.text}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Issuer</TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                    {tableUserDeal.issuerName.text}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Issue</TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                    {tableUserDeal.issueDescription.text}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>State</TableCell>
                  <TableCell>{tableUserDeal.state.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Amount</TableCell>
                  <TableCell>{tableUserDeal.amount.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Security</TableCell>
                  <TableCell>{tableUserDeal.securityName.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Spline Rating</TableCell>
                  <TableCell>{tableUserDeal.rating.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Offering Date</TableCell>
                  <TableCell>{tableUserDeal.offeringDate.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Dated Date</TableCell>
                  <TableCell>{tableUserDeal.datedDate.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Call Date</TableCell>
                  <TableCell>{tableUserDeal.callDate.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>State Enhancement</TableCell>
                  <TableCell>{tableUserDeal.enhancement.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Bond Insurance</TableCell>
                  <TableCell>{tableUserDeal.insurance.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Sale Method</TableCell>
                  <TableCell>{tableUserDeal.saleMethod.text}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500 }}>Tax Designation</TableCell>
                  <TableCell>{tableUserDeal.taxCode.text}</TableCell>
                </TableRow>
              </Table>
            </TableContainer>

            <TableContainer sx={{ width: "100%" }}>
              <Table sx={{ borderTop: "1px solid #e0e0e0" }}>
                <TableHead>
                  <TableCell sx={{ fontWeight: 500 }}>{" "}</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Maturity Year</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Maturity Amount</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>T</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Coupon</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Yield</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>AAA</TableCell>
                  <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Spread (bps)</TableCell>
                </TableHead>
                <TableBody>
                  {range(smallerYear, biggerYear).map((year, index) => {
                    const alignment = year - new Date().getFullYear();
                    
                    return (
                      <TableRow key={index}>
                        <TableCell align="right" sx={{ border: "1px solid #e0e0e0", borderLeft: 0, width: "20px", padding: "0px 4px" }}>{alignment < 1 ? "<1" : alignment}</TableCell>
                        <TableCell align="center" width="32px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "maturityDates").text}</TableCell>
                        <TableCell align="right" width="72px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "principals").text}</TableCell>
                        <TableCell align="center" width="9px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "sinkFunds").text}</TableCell>
                        <TableCell align="center" width="65px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "coupons").text}</TableCell>
                        <TableCell align="center" width="45px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "offeringYields").text}</TableCell>
                        <TableCell align="center" width="40px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "splineAAAs").text}</TableCell>
                        <TableCell align="center" width="60px" sx={{ px: "6px" }}>{getDealData(tableUserDeal, year, "spreads").text}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          <Stack direction="row" sx={{ overflowX: "auto" }}>
            {tableSimilarDeals.map((deal, index) => {
              return (
                <Stack direction="column" gap={2} width="530px" sx={{ borderRight: "1px solid #e0e0e0", flexShrink: 0 }}>
                  <TableContainer sx={{ width: "100%", maxWidth: "100%" }}>
                    <Table>
                      <TableRow>
                        <TableCell 
                          colSpan={2} 
                          align="center" 
                          sx={{ 
                            fontWeight: 500, 
                            py: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >{deal.issuerName.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Issuer</TableCell>
                        <TableCell 
                          sx={{ 
                            background: deal.issuerName.bg, 
                            color: (BG_COLOR_MAP as any)[deal.issuerName.bg],
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >{deal.issuerName.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, minWidth: "130px" }}>Issue</TableCell>
                        <TableCell 
                          sx={{ 
                            background: deal.issueDescription.bg, 
                            color: (BG_COLOR_MAP as any)[deal.issueDescription.bg],
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >{deal.issueDescription.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>State</TableCell>
                        <TableCell sx={{ background: deal.state.bg, color: (BG_COLOR_MAP as any)[deal.state.bg] }}>{deal.state.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Amount</TableCell>
                        <TableCell sx={{ background: deal.amount.bg, color: (BG_COLOR_MAP as any)[deal.amount.bg] }}>{deal.amount.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Security</TableCell>
                        <TableCell sx={{ background: deal.securityName.bg, color: (BG_COLOR_MAP as any)[deal.securityName.bg] }}>{deal.securityName.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Spline Rating</TableCell>
                        <TableCell sx={{ background: deal.rating.bg, color: (BG_COLOR_MAP as any)[deal.rating.bg] }}>{deal.rating.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Offering Date</TableCell>
                        <TableCell sx={{ background: deal.offeringDate.bg, color: (BG_COLOR_MAP as any)[deal.offeringDate.bg] }}>{deal.offeringDate.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Dated Date</TableCell>
                        <TableCell sx={{ background: deal.datedDate.bg, color: (BG_COLOR_MAP as any)[deal.datedDate.bg] }}>{deal.datedDate.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Call Date</TableCell>
                        <TableCell sx={{ background: deal.callDate.bg, color: (BG_COLOR_MAP as any)[deal.callDate.bg] }}>{deal.callDate.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>State Enhancement</TableCell>
                        <TableCell sx={{ background: deal.enhancement.bg, color: (BG_COLOR_MAP as any)[deal.enhancement.bg] }}>{deal.enhancement.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Bond Insurance</TableCell>
                        <TableCell sx={{ background: deal.insurance.bg, color: (BG_COLOR_MAP as any)[deal.insurance.bg] }}>{deal.insurance.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Sale Method</TableCell>
                        <TableCell sx={{ background: deal.saleMethod.bg, color: (BG_COLOR_MAP as any)[deal.saleMethod.bg] }}>{deal.saleMethod.text}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500 }}>Tax Designation</TableCell>
                        <TableCell sx={{ background: deal.taxCode.bg, color: (BG_COLOR_MAP as any)[deal.taxCode.bg] }}>{deal.taxCode.text}</TableCell>
                      </TableRow>
                    </Table>
                  </TableContainer>

                  <TableContainer sx={{ width: "100%" }}>
                    <Table sx={{ borderTop: "1px solid #e0e0e0" }}>
                      <TableHead>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Maturity Year</TableCell>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Maturity Amount</TableCell>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>T</TableCell>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Coupon</TableCell>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Yield</TableCell>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>AAA</TableCell>
                        <TableCell sx={{ fontWeight: 500, textAlign: "center", lineHeight: "18px" }}>Spread (bps)</TableCell>
                      </TableHead>
                      <TableBody>
                        {range(smallerYear, biggerYear).map((year, index) => {
                          const maturityData = getDealData(deal, year, "maturityDates");
                          const principalsData = getDealData(deal, year, "principals");
                          const sinkFundsData = getDealData(deal, year, "sinkFunds");
                          const couponsData = getDealData(deal, year, "coupons");
                          const offeringYieldsData = getDealData(deal, year, "offeringYields");
                          const splineAAAsData = getDealData(deal, year, "splineAAAs");
                          const spreadsData = getDealData(deal, year, "spreads");

                          return (
                            <TableRow key={index}>
                              <TableCell align="center" width="32px" sx={{ px: "6px", background: maturityData.bg, color: (BG_COLOR_MAP as any)[maturityData.bg] }}>{maturityData.text || <>&nbsp;</>}</TableCell>
                              <TableCell align="right" width="72px" sx={{ px: "6px", background: principalsData.bg, color: (BG_COLOR_MAP as any)[principalsData.bg] }}>{principalsData.text}</TableCell>
                              <TableCell align="center" width="9px" sx={{ px: "6px", background: sinkFundsData.bg, color: (BG_COLOR_MAP as any)[sinkFundsData.bg] }}>{sinkFundsData.text}</TableCell>
                              <TableCell align="center" width="65px" sx={{ px: "6px", background: couponsData.bg, color: (BG_COLOR_MAP as any)[couponsData.bg] }}>{couponsData.text}</TableCell>
                              <TableCell align="center" width="45px" sx={{ px: "6px", background: offeringYieldsData.bg, color: (BG_COLOR_MAP as any)[offeringYieldsData.bg] }}>{offeringYieldsData.text}</TableCell>
                              <TableCell align="center" width="40px" sx={{ px: "6px", background: splineAAAsData.bg, color: (BG_COLOR_MAP as any)[splineAAAsData.bg] }}>{splineAAAsData.text}</TableCell>
                              <TableCell align="center" width="60px" sx={{ px: "6px", background: spreadsData.bg, color: (BG_COLOR_MAP as any)[spreadsData.bg] }}>{spreadsData.text}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )
            })}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}